import { useCallback, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import SOURCE_TYPE from '../SourceCreate.constants';
import { useSourceCreateState } from '../SourceCreate.hooks';

/**
 * Validates and applies category-specific rules for cloud sources.
 *
 * Currently handles:
 * - CrowdStrike CSPM IOA Streaming Event cloud provider requirement
 */
const useCategoryValidations = () => {
  const { setValue } = useFormContext();
  const {
    selectedSourceTypeId,
    selectedSourceDetails,
    categories: selectedCategories,
    setCloudProvider
  } = useSourceCreateState();

  /**
   * Checks if the current configuration is a CrowdStrike CSPM IOA Streaming Event.
   *
   * Business Rule:
   * CrowdStrike's Cloud Security Posture Management (CSPM) Indicator of Attack (IOA)
   * streaming events require cloud provider specification because these events are
   * cloud-infrastructure specific (AWS, Azure, GCP). This function identifies when
   * this category is selected so we can enforce the cloud_provider field requirement.
   *
   * @returns true if CrowdStrike source with CSPMIOAStreamingEvent category selected
   */
  const isCrowdStrikeCSPMIOAEvent = useCallback((): boolean => {
    const isCrowdStrikeSource =
      selectedSourceTypeId === SOURCE_TYPE.CROWD_STRIKE ||
      Number(selectedSourceDetails?.source_type_id) ===
        SOURCE_TYPE.CROWD_STRIKE;

    const hasCSPMIOACategory =
      selectedCategories?.includes('CSPMIOAStreamingEvent') ?? false;

    return isCrowdStrikeSource && hasCSPMIOACategory;
  }, [selectedCategories, selectedSourceTypeId, selectedSourceDetails]);

  /**
   * Auto-populates cloud_provider field when loading existing CrowdStrike CSPM IOA sources.
   *
   * Business Rule:
   * When editing an existing CrowdStrike CSPM IOA source, we must restore the previously
   * selected cloud provider(s) to both the form and the state. This ensures:
   * 1. The form shows the correct existing values
   * 2. Validation passes (cloud_provider is required for this category)
   * 3. The user can modify cloud providers if needed
   */
  useEffect(() => {
    if (isCrowdStrikeCSPMIOAEvent() && selectedSourceDetails?.cloud_provider) {
      const cloudProviders = selectedSourceDetails.cloud_provider || [];

      // Update both form state and Jotai state to keep them synchronized
      setCloudProvider(cloudProviders);
      setValue('cloud_provider', cloudProviders);
    }
  }, [
    selectedCategories,
    isCrowdStrikeCSPMIOAEvent,
    selectedSourceDetails?.cloud_provider,
    setCloudProvider,
    setValue
  ]);

  return {
    isCrowdStrikeCSPMIOAEvent
  };
};

export default useCategoryValidations;
